/**
 * Re-sync of the notification list after a realtime (re)connect (#1090).
 *
 * The client asks cdb_tqs for everything created after the newest notification
 * it holds and merges that in. Read states and deletions that changed while it
 * was offline do not show up in such a delta, so the server's unread count is
 * compared with what the list would say after the merge; a difference means
 * the list drifted and is reloaded as a whole.
 */
import type { Notification } from '@/services/notification-service'

const time = (value: string): number => {
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

/**
 * The newest `created_at`, as the server wrote it. Returned verbatim: a round
 * trip through `Date` would cut microseconds and re-fetch the newest row.
 */
export function newestCreatedAt(notifications: readonly Notification[]): string | null {
  let newest: Notification | null = null
  for (const n of notifications) {
    if (!n.created_at) continue
    if (!newest || time(n.created_at) > time(newest.created_at)) newest = n
  }
  return newest?.created_at ?? null
}

/** The arrivals in front, deduplicated by id (the arrival wins), newest first. */
export function mergeArrivals(
  current: readonly Notification[],
  arrivals: readonly Notification[],
): Notification[] {
  const byId = new Map<string, Notification>()
  for (const n of current) byId.set(n.id, n)
  for (const n of arrivals) byId.set(n.id, n)
  return [...byId.values()].sort((a, b) => time(b.created_at) - time(a.created_at))
}

/**
 * Whether anything besides new arrivals changed while offline: the unread
 * count the list had, plus the unread arrivals it did not know yet, must equal
 * the server's count.
 */
export function readStateDrifted(
  localUnread: number,
  current: readonly Notification[],
  arrivals: readonly Notification[],
  serverUnread: number,
): boolean {
  const known = new Set(current.map((n) => n.id))
  const newUnread = arrivals.filter((n) => !n.is_read && !known.has(n.id)).length
  return localUnread + newUnread !== serverUnread
}
